import { useId, useMemo, useState } from 'react';
// Through the local shim, never react-i18next directly: a host app augments
// CustomTypeOptions, and importing the library here would type-check this lib's keys
// against the host's own union.
import { useTranslation } from '../i18n'
import { COLORS } from '../lib/theme'
import {
  GROUND_DEFAULT_COLOR,
  FILLED_WASH_FILL,
  FILLED_WASH_STROKE,
  REVERSE_WASH_FILL,
  REVERSE_WASH_STROKE,
} from '../lib/mapBorders';
import { Icon } from './Icon'
import { pinSrcFor } from './MapPin'
import { Switch } from './Switch'
import { usePinTypeCatalog } from './PinTypeCatalogProvider'
import type { PinTypeEntry } from '../lib/pinTypeCatalog'
import type { MapAppearance, BorderAppearance } from '../lib/mapAppearance'

type Props = {
  value: MapAppearance
  onChange: (next: MapAppearance) => void
  onClose: () => void
/**
 * Which edge the panel anchors to. Defaults to the right, where the map control
 * stack is. The print surface anchors these left, because its print panel holds
 * the right slot permanently and would otherwise be buried by them.
 */
  side?: 'left' | 'right'
  /**
   * Pixels to drop the panel by, so it stacks beneath whatever the surface has
   * already put in this corner — today the hidden-pin-types chip. The host owns
   * the number because only it knows what else is up there.
   */
  topOffset?: number
  /**
   * Whether the border-style picker (standard / reverse / filled) is offered.
   * Defaults to true. The map editor passes false: like the app's planning
   * map it draws the standard treatment regardless of the setting, and a
   * control that changes nothing on the surface it sits on reads as broken.
   * The value itself is still shared storage, so a choice made on the team
   * map holds when the editor is opened and closed again.
   */
  borderStyles?: boolean
}

// Small preview glyphs matching the wetrack design system's normal/reverse/filled
// border icons. Each glyph is a 32×24 rounded outline, with no/red/green fill.
const borderOptions = (
  idPrefix: string,
): { id: BorderAppearance; labelKey: string; glyph: JSX.Element }[] => [
  {
    id: 'standard',
    // A key, not a resolved string: this stays a module-level function so it can be
    // evaluated without a React context, and the caller is the only place that has `t`.
    labelKey: 'appearance.mode.standard',
    glyph: (
      <svg width="36" height="22" viewBox="0 0 36 22" fill="none" aria-hidden="true">
        <rect x="2.5" y="2.5" width="31" height="17" rx="3" stroke={GROUND_DEFAULT_COLOR} strokeWidth="2" fill="none" />
      </svg>
    ),
  },
  {
    id: 'reverse',
    labelKey: 'appearance.mode.reverse',
    glyph: (
      // Red rectangle filling the canvas with a clear cut-out rect in the
      // middle showing through — represents "everything outside the hunting
      // ground is forbidden". Mirrors wetrack's reverse rendering.
      <svg width="36" height="22" viewBox="0 0 36 22" fill="none" aria-hidden="true">
        <mask id={`${idPrefix}-reverse-glyph-mask`}>
          <rect x="0" y="0" width="36" height="22" fill="white" />
          <rect x="6.5" y="4.5" width="23" height="13" rx="2" fill="black" />
        </mask>
        <rect x="0" y="0" width="36" height="22" fill={REVERSE_WASH_FILL} mask={`url(#${idPrefix}-reverse-glyph-mask)`} />
        <rect x="6.5" y="4.5" width="23" height="13" rx="2" stroke={REVERSE_WASH_STROKE} strokeWidth="2" fill="none" />
      </svg>
    ),
  },
  {
    id: 'filled',
    labelKey: 'appearance.mode.filled',
    glyph: (
      <svg width="36" height="22" viewBox="0 0 36 22" fill="none" aria-hidden="true">
        <rect x="2.5" y="2.5" width="31" height="17" rx="3" fill={FILLED_WASH_FILL} stroke={FILLED_WASH_STROKE} strokeWidth="2" />
      </svg>
    ),
  },
]

export function MapAppearancePanel({
  value,
  onChange,
  onClose,
  side = 'right',
  topOffset = 0,
  borderStyles = true,
}: Props) {
  // Unique per mount: the reverse swatch carries a mask, mask ids are
  // document-global, and this panel is mounted from two hosts at once.
  const idPrefix = useId();
  const { t } = useTranslation('map');
  const catalog = usePinTypeCatalog()
  const update = <K extends keyof MapAppearance>(key: K, next: MapAppearance[K]) =>
    onChange({ ...value, [key]: next })

  const updateLine = (key: keyof MapAppearance['lineThickness'], next: number) =>
    onChange({ ...value, lineThickness: { ...value.lineThickness, [key]: next } })

  // CLUB-667: the panel is otherwise fully controlled, and stays so for
  // everything that is a *setting*. Which groups are open and what is typed in
  // the search box are not settings — they are where you are in the list right
  // now. Routing them through MapAppearance would persist them to localStorage
  // and re-open the same groups on the print surface, so they live here.
  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<string>>(EMPTY_GROUPS)
  const [pinQuery, setPinQuery] = useState('')

  // Not memoised: `clampAppearance` returns a fresh `hiddenPinTypes` array on
  // every render, so a useMemo keyed on it could never hit — it would rebuild
  // the Set anyway and only read as though it were an optimisation.
  const hidden = new Set(value.hiddenPinTypes)

  const setHidden = (next: Iterable<string>) =>
    onChange({ ...value, hiddenPinTypes: [...next] })

  const togglePinType = (type: string, visible: boolean) => {
    const next = new Set(hidden)
    if (visible) next.delete(type)
    else next.add(type)
    setHidden(next)
  }

  const toggleGroup = (id: string) =>
    setExpandedGroups((open) => {
      const next = new Set(open)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  // Matched against the pin's own label only — never the group name, matching
  // mobile: typing "animals" is not a way to select the Animals group.
  const query = pinQuery.trim().toLowerCase()
  const matches = useMemo(
    // Against the LOCALIZED name, not the English label: a Swedish user searches
    // "älg", not "moose". Resolving through the catalog's own label is what
    // makes the search agree with what the rows actually read.
    () => (query ? catalog.leaves.filter((e) => catalog.label(e.id).toLowerCase().includes(query)) : []),
    [query, catalog],
  )

  return (
    <>
      <div
        data-map-print="hide"
        data-testid="map-appearance-backdrop"
        style={{ position: 'absolute', inset: 0, zIndex: 9 }}
        onClick={onClose}
      />

      <div
        data-map-print="hide"
        style={{
          position: 'absolute',
          top: 16 + topOffset,
          ...(side === 'left' ? { left: 16 } : { right: 60 }),
          zIndex: 10,
          width: 280,
          maxHeight: `calc(100% - ${32 + topOffset}px)`,
          background: COLORS.surfaceContainerLowest,
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: '0 4px 24px rgba(0,0,0,0.28)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Header title={t('appearance.title')} subtitle={t('appearance.subtitle')} />

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {borderStyles && (
          <Section title={t('appearance.sections.border')}>
            <div style={{ display: 'flex', gap: 12 }}>
              {borderOptions(idPrefix).map((opt) => {
                const active = value.borderAppearance === opt.id
                const label = t(opt.labelKey)
                return (
                  <button
                    key={opt.id}
                    type="button"
                    aria-label={label}
                    aria-pressed={active}
                    onClick={() => update('borderAppearance', opt.id)}
                    style={{
                      flex: 1,
                      minHeight: 56,
                      padding: '6px 4px',
                      borderRadius: 8,
                      border: active
                        ? `2px solid ${COLORS.primary}`
                        : `1.5px solid ${COLORS.surfaceContainerHighest}`,
                      background: COLORS.surfaceContainerLowest,
                      color: COLORS.onSurface,
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: 'inherit',
                    }}
                  >
                    {opt.glyph}
                    {/* Visible, not only an aria-label. The three glyphs are the only
                        thing distinguishing three treatments that render very
                        differently, and a support screenshot of this panel could not
                        previously say which mode the map was in. */}
                    <span style={{ fontSize: 10, lineHeight: 1.15, textAlign: 'center' }}>
                      {label}
                    </span>
                  </button>
                )
              })}
            </div>
          </Section>
          )}

          <Section title={t('appearance.sections.backgrounds')}>
            <SwitchRow
              label={t('appearance.pinBackground.label')}
              hint={t('appearance.pinBackground.hint')}
              // Deliberately shorter than the visible label: the accessible name is
              // what a control is called, not a description of it.
              ariaLabel={t('appearance.pinBackground.aria')}
              on={value.pinBackground}
              onChange={(v) => update('pinBackground', v)}
            />
            <SwitchRow
              label={t('appearance.labelBackground.label')}
              hint={t('appearance.labelBackground.hint')}
              ariaLabel={t('appearance.labelBackground.label')}
              on={value.labelBackground}
              onChange={(v) => update('labelBackground', v)}
            />
          </Section>

          {/*
            CLUB-667: every surface gets these. The editor used to be excluded
            because it drew its own layers and would not obey the switches —
            it obeys them now, so one set of view settings holds across the Map
            tab, the print sheet and the editor.
          */}
          {/*
            The app's view-settings switches, in the app's order: forbidden areas,
            sub-areas, arrows, lines (drive's `filtering_widget.dart`).
          */}
          <Section title={t('appearance.sections.layers')}>
            <SwitchRow
              // Its own label rather than `layer.forbidden`: that key is the editor
              // tab's short "Forbidden", which reads as an adjective with nothing to
              // qualify on a switch. The wording is the GPX import's existing
              // "Forbidden areas", so no locale gains a second term for the layer.
              label={t('appearance.forbiddenAreas.label')}
              hint={t('appearance.forbiddenAreas.hint')}
              ariaLabel={t('appearance.forbiddenAreas.label')}
              on={value.forbiddenAreasVisible}
              onChange={(v) => update('forbiddenAreasVisible', v)}
            />
            <SwitchRow
              // The existing layer vocabulary, reused rather than duplicated: minting
              // a second word for one concept per locale is what a terminology gate
              // exists to prevent.
              label={t('layer.subareas')}
              hint={t('appearance.subAreas.hint')}
              ariaLabel={t('layer.subareas')}
              on={value.subAreasVisible}
              onChange={(v) => update('subAreasVisible', v)}
            />
            <SwitchRow
              label={t('layer.arrows')}
              hint={t('appearance.arrows.hint')}
              ariaLabel={t('layer.arrows')}
              on={value.arrowsVisible}
              onChange={(v) => update('arrowsVisible', v)}
            />
            <SwitchRow
              label={t('layer.lines')}
              hint={t('appearance.lines.hint')}
              ariaLabel={t('layer.lines')}
              on={value.linesVisible}
              onChange={(v) => update('linesVisible', v)}
            />

            {/*
              CLUB-667: the pin-type filter. It reaches every surface, the
              editor included — see the note on the section above. The editor
              carries the guard rails that go with that: placing a pin of a
              hidden type shows the type again, and hiding the selected pin's
              type clears the selection (MapEditorPage: addObj, and the effect
              beside `selected`).
            */}
            <div style={{ height: 1, background: COLORS.surfaceContainerHighest, margin: '2px 0' }} />

            <div style={{
              fontSize: 11, fontWeight: 700, color: COLORS.onSurfaceVariant,
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>{t('appearance.pinTypes.title')}</div>

            <SwitchRow
              label={t('appearance.pinTypes.all')}
              hint={
                hidden.size === 0
                  ? t('appearance.pinTypes.allShown')
                  : t('appearance.pinTypes.someHidden', {
                      count: hidden.size,
                      total: catalog.leaves.length,
                    })
              }
              ariaLabel={t('appearance.pinTypes.all')}
              on={hidden.size === 0}
              onChange={(v) => setHidden(v ? [] : catalog.leaves.map((e) => e.id))}
            />

            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                color: COLORS.outline, display: 'flex', pointerEvents: 'none',
              }}>
                <Icon name="search" size={14} />
              </span>
              <input
                type="search"
                value={pinQuery}
                onChange={(e) => setPinQuery(e.target.value)}
                placeholder={t('appearance.pinTypes.search')}
                aria-label={t('appearance.pinTypes.search')}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '7px 10px 7px 30px',
                  fontSize: 12, borderRadius: 999, outline: 'none',
                  border: `1.5px solid ${COLORS.outlineVariant}`,
                  background: COLORS.surfaceContainerLowest, color: COLORS.onSurface,
                }}
              />
            </div>

            {/*
              No scroller of its own: the panel body is already one, and a
              bounded box here put a second scrollbar inside the first as soon
              as a group was expanded. Groups are collapsed by default, so the
              resting height is three rows; an expanded group simply makes the
              panel's own scroll longer, which is what that scroll is for.
            */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {query ? (
                matches.length === 0 ? (
                  <div style={{ fontSize: 12, color: COLORS.onSurfaceVariant, padding: '8px 0' }}>
                    {t('appearance.pinTypes.noMatches', { query: pinQuery.trim() })}
                  </div>
                ) : (
                  // Matches render flat, with no group headers — mobile's
                  // behaviour: while searching you are picking a type, not
                  // navigating the taxonomy.
                  matches.map((entry) => (
                    <PinTypeRow
                      key={entry.id}
                      type={entry.id}
                      name={catalog.label(entry.id)}
                      indent={false}
                      withBackground={value.pinBackground}
                      on={!hidden.has(entry.id)}
                      onChange={(v) => togglePinType(entry.id, v)}
                    />
                  ))
                )
              ) : (
                catalog.entries.map((entry) => {
                  if (entry.members.length === 0) {
                    return (
                      <PinTypeRow
                        key={entry.id}
                        type={entry.id}
                        name={catalog.label(entry.id)}
                        indent={false}
                        withBackground={value.pinBackground}
                        on={!hidden.has(entry.id)}
                        onChange={(v) => togglePinType(entry.id, v)}
                      />
                    )
                  }
                  const visibleCount = entry.members.filter((m) => !hidden.has(m.id)).length
                  const isOpen = expandedGroups.has(entry.id)
                  return (
                    <div key={entry.id}>
                      <button
                        type="button"
                        onClick={() => toggleGroup(entry.id)}
                        aria-expanded={isOpen}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                          padding: '7px 0', background: 'none', border: 'none',
                          cursor: 'pointer', color: COLORS.onSurface, textAlign: 'left',
                        }}
                      >
                        <span style={{ display: 'flex', color: COLORS.onSurfaceVariant }}>
                          <Icon name={isOpen ? 'chevron-down' : 'chevron-right'} size={14} />
                        </span>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600 }}>
                          {catalog.label(entry.id)}
                        </span>
                        {/*
                          A partly-hidden group needs to say so somewhere, and
                          the count says it exactly — no third switch state,
                          and it reads out as text to a screen reader.
                        */}
                        <span style={{ fontSize: 11, color: COLORS.onSurfaceVariant }}>
                          {visibleCount === entry.members.length
                            ? `${entry.members.length}`
                            : `${visibleCount}/${entry.members.length}`}
                        </span>
                      </button>
                      {isOpen && entry.members.map((member) => (
                        <PinTypeRow
                          key={member.id}
                          type={member.id}
                          name={catalog.label(member.id)}
                          indent
                          withBackground={value.pinBackground}
                          on={!hidden.has(member.id)}
                          onChange={(v) => togglePinType(member.id, v)}
                        />
                      ))}
                    </div>
                  )
                })
              )}
            </div>
          </Section>

          <Section title={t('appearance.sections.labels')}>
            <SwitchRow
              label={t('appearance.pinNames.label')}
              hint={t('appearance.pinNames.hint')}
              ariaLabel={t('appearance.pinNames.label')}
              on={value.pinNamesVisible}
              onChange={(v) => update('pinNamesVisible', v)}
            />
          </Section>

          <Section title={t('appearance.sections.sizes')}>
            <SliderRow
              label={t('appearance.pinSize')}
              min={0.5}
              max={2.0}
              step={0.1}
              value={value.pinScale}
              onChange={(v) => update('pinScale', v)}
            />
          </Section>

          <Section title={t('appearance.sections.transparency')}>
            <SliderRow
              label={t('appearance.pinOpacity')}
              min={25}
              max={100}
              step={1}
              value={value.pinOpacity}
              onChange={(v) => update('pinOpacity', v)}
              suffix="%"
            />
            <SliderRow
              label={t('appearance.labelOpacity')}
              min={25}
              max={100}
              step={1}
              value={value.labelOpacity}
              onChange={(v) => update('labelOpacity', v)}
              suffix="%"
            />
          </Section>

          <Section title={t('appearance.sections.lineThickness')}>
            <SliderRow label={t('layer.border')} min={0.5} max={2.0} step={0.1}
              value={value.lineThickness.border} onChange={(v) => updateLine('border', v)} />
            {/* The plural under a per-layer heading, reusing the layer vocabulary rather
                than minting a singular of it in seven languages. */}
            <SliderRow label={t('layer.subareas')} min={0.5} max={2.0} step={0.1}
              value={value.lineThickness.subarea} onChange={(v) => updateLine('subarea', v)} />
            <SliderRow label={t('layer.forbidden')} min={0.5} max={2.0} step={0.1}
              value={value.lineThickness.forbidden} onChange={(v) => updateLine('forbidden', v)} />
          </Section>
        </div>
      </div>
    </>
  )
}

// Resolved strings rather than a hook of its own: `t` lives at the one call site that
// knows which keys apply, and four leaf components each reaching for a context would be
// four more places to get the namespace wrong.
function Header({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div style={{ padding: '14px 16px 12px', borderBottom: `1px solid ${COLORS.surfaceContainerHighest}` }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.onSurface }}>{title}</div>
      <div style={{ fontSize: 12, color: COLORS.onSurfaceVariant, marginTop: 1 }}>
        {subtitle}
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.surfaceContainerHighest}` }}>
      <div style={{
        fontSize: 12, fontWeight: 700, color: COLORS.onSurface,
        marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em',
      }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{children}</div>
    </div>
  )
}

function SwitchRow({
  label, hint, ariaLabel, on, onChange,
}: {
  label: string
  hint?: string
  ariaLabel: string
  on: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.onSurface }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: COLORS.onSurfaceVariant, marginTop: 1 }}>{hint}</div>}
      </div>
      <Switch aria-label={ariaLabel} on={on} onChange={onChange} />
    </label>
  )
}

/** Stable empty set, so the initial state does not allocate on every render. */
const EMPTY_GROUPS: ReadonlySet<string> = new Set<string>()

/**
 * CLUB-667: one pin type in the filter list.
 *
 * Not a `SwitchRow`: that row carries a hint line and a 12px gap tuned for a
 * handful of settings, where this is a dense 43-row list. Teaching SwitchRow an
 * `indent`/`compact` mode would leak list concerns into the general row.
 */
function PinTypeRow({
  type, name, indent, withBackground, on, onChange,
}: {
  type: string
  /** The pin type's localized display name, resolved by the parent (which holds
   *  the catalog); this leaf stays context-free like the other rows in this file. */
  name: string
  indent: boolean
  /** Follows the panel's own pin-background switch, so the list shows the same
   *  drawing the map will use — the picker agreeing with what it picks for. */
  withBackground: boolean
  on: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label style={{
      display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
      padding: '3px 0', paddingLeft: indent ? 22 : 0,
    }}>
      {/* The pin's own artwork, not a colour swatch: on a dense ground you are
          looking for the thing you can see on the map, and 43 coloured dots are
          not distinguishable from each other. Dimmed while hidden, so the row
          reads as off at a glance. */}
      <img
        src={pinSrcFor(type, withBackground)}
        width={18}
        height={18}
        alt=""
        aria-hidden
        style={{ flex: '0 0 auto', objectFit: 'contain', opacity: on ? 1 : 0.35 }}
      />
      <span
        title={name}
        style={{
          flex: 1, minWidth: 0, fontSize: 12.5, color: COLORS.onSurface,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}
      >{name}</span>
      <Switch aria-label={name} on={on} onChange={onChange} />
    </label>
  )
}

function SliderRow({
  label, min, max, step, value, onChange, suffix,
}: {
  label: string
  min: number
  max: number
  step: number
  value: number
  onChange: (v: number) => void
  suffix?: string
}) {
  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontSize: 12, fontWeight: 600, color: COLORS.onSurface, marginBottom: 4,
      }}>
        <span>{label}</span>
        <span style={{ color: COLORS.onSurfaceVariant, fontWeight: 500 }}>
          {suffix === '%' ? `${Math.round(value)}%` : `${value.toFixed(1)}×`}
        </span>
      </div>
      <input
        type="range"
        aria-label={label}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: '100%', accentColor: COLORS.primary }}
      />
    </div>
  )
}
